import React, { useState, useEffect } from 'react';
import { Card, message, Spin, Button, Input } from 'antd';
import { useParams, useNavigate } from 'react-router-dom';
import { matchApi } from '../../services/api';
import './style.css';

const { TextArea } = Input;

const EditMatch = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [record, setRecord] = useState(null);
  const [questionContent, setQuestionContent] = useState('');
  const [answerContent, setAnswerContent] = useState('');
  const [saveTimer, setSaveTimer] = useState(null);
  const [isComposing, setIsComposing] = useState(false);

  useEffect(() => {
    const fetchRecord = async () => {
      console.log('开始获取匹配记录，ID:', id);
      try {
        console.log('调用API获取记录详情...');
        const response = await matchApi.getRecordDetail(id);
        console.log('API响应数据:', response);
        
        if (!response) {
          console.error('API返回空响应');
          message.error('获取匹配记录失败：服务器返回空响应');
          return;
        }
        
        // 直接使用response，因为API返回的数据就是直接的对象
        console.log('设置记录数据:', response);
        setRecord(response);
        
        // 添加空值检查
        const questionContent = response.question_content || '';
        const answerContent = response.answer_content || '';
        
        console.log('设置问题内容:', questionContent);
        console.log('设置答案内容:', answerContent);
        
        setQuestionContent(questionContent);
        setAnswerContent(answerContent);
      } catch (error) {
        console.error('获取匹配记录失败:', error);
        console.error('错误详情:', {
          message: error.message,
          status: error.response?.status,
          data: error.response?.data
        });
        message.error(`获取匹配记录失败: ${error.message || '未知错误'}`);
      } finally {
        console.log('加载完成，设置loading为false');
        setLoading(false);
      }
    };

    fetchRecord();
  }, [id]);

  // 添加防抖保存功能
  const debounceSave = (question, answer) => {
    console.log('触发防抖保存');
    if (saveTimer) {
      console.log('清除之前的保存定时器');
      clearTimeout(saveTimer);
    }
    const timer = setTimeout(() => {
      console.log('执行保存操作');
      saveContent(question, answer);
    }, 2000);
    setSaveTimer(timer);
  };

  const handleQuestionChange = (e) => {
    console.log('问题内容变更:', e.target.value);
    const value = e.target.value;
    setQuestionContent(value);
    if (!isComposing) {
      console.log('非输入法状态，触发保存');
      debounceSave(value, answerContent);
    }
  };

  const handleAnswerChange = (e) => {
    console.log('答案内容变更:', e.target.value);
    const value = e.target.value;
    setAnswerContent(value);
    if (!isComposing) {
      console.log('非输入法状态，触发保存');
      debounceSave(questionContent, value);
    }
  };

  // 处理输入法开始输入
  const handleCompositionStart = () => {
    console.log('输入法开始输入');
    setIsComposing(true);
  };

  // 处理输入法结束输入
  const handleCompositionEnd = (e, isQuestion = true) => {
    console.log('输入法结束输入');
    setIsComposing(false);
    if (isQuestion) {
      console.log('问题输入法结束，触发保存');
      debounceSave(e.target.value, answerContent);
    } else {
      console.log('答案输入法结束，触发保存');
      debounceSave(questionContent, e.target.value);
    }
  };

  const saveContent = async (question, answer) => {
    console.log('开始保存内容:', { question, answer });
    try {
      const response = await matchApi.updateRecord(id, {
        question_content: question,
        answer_content: answer
      });
      console.log('保存成功，响应:', response);
    } catch (error) {
      console.error('保存内容失败:', error);
      console.error('错误详情:', {
        message: error.message,
        status: error.response?.status,
        data: error.response?.data
      });
      message.error(`保存失败: ${error.message || '未知错误'}`);
    }
  };

  const handleBack = () => {
    console.log('返回列表');
    if (saveTimer) {
      console.log('清除保存定时器并执行最后一次保存');
      clearTimeout(saveTimer);
      saveContent(questionContent, answerContent);
    }
    navigate('/match');
  };

  if (loading) {
    console.log('显示加载中状态');
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!record) {
    console.log('记录不存在，显示错误状态');
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <h2>未找到匹配记录</h2>
        <Button type="primary" onClick={handleBack}>返回列表</Button>
      </div>
    );
  }

  console.log('渲染编辑界面');
  return (
    <div className="edit-match-container">
      <Card 
        title={`编辑匹配内容 - ${record.subject}`}
        extra={<Button onClick={handleBack}>返回列表</Button>}
      >
        {record.match_type === '题答分离' ? (
          <div className="split-editor">
            <div className="question-editor">
              <h3>题目内容</h3>
              <TextArea 
                value={questionContent}
                onChange={handleQuestionChange}
                onCompositionStart={handleCompositionStart}
                onCompositionEnd={(e) => handleCompositionEnd(e, true)}
                placeholder="请输入题目内容"
                autoSize={{ minRows: 20, maxRows: 30 }}
                style={{ marginBottom: '20px' }}
              />
            </div>
            <div className="answer-editor">
              <h3>答案内容</h3>
              <TextArea 
                value={answerContent}
                onChange={handleAnswerChange}
                onCompositionStart={handleCompositionStart}
                onCompositionEnd={(e) => handleCompositionEnd(e, false)}
                placeholder="请输入答案内容"
                autoSize={{ minRows: 20, maxRows: 30 }}
              />
            </div>
          </div>
        ) : (
          <div className="integrated-editor">
            <h3>题目答案内容</h3>
            <TextArea 
              value={questionContent}
              onChange={handleQuestionChange}
              onCompositionStart={handleCompositionStart}
              onCompositionEnd={(e) => handleCompositionEnd(e, true)}
              placeholder="请输入题目答案内容"
              autoSize={{ minRows: 25, maxRows: 35 }}
            />
          </div>
        )}
      </Card>
    </div>
  );
};

export default EditMatch; 